import {useFormatCurrency} from 'platform/locale';

import {VehicleSelectionOutcomeResponseBody} from '@dms/api/sales';
import {useTenant} from '@dms/shared';

import {useVehicleSelectionFilterOptions} from '../../../../../hooks/useVehicleSelectionFilterOptions';
import {fromMinorUnits} from '../../../../../utils/fromMinorUnits';
import {getCurrencyFractionDigits} from '../../../../../utils/getCurrencyFractionDigits';
import {formatVehicleSelectionOutcomeValue} from '../utils/formatVehicleSelectionOutcomeValue';
import {stripVehicleSelectionCodePrefix} from '../utils/stripVehicleSelectionCodePrefix';

// The one attribute the catalog reports in minor units.
const PRICE_ATTRIBUTE = 'PRICE';

type LabelledOption = {value: string; label: string | null};

const toLabels = (options: readonly LabelledOption[]) =>
  new Map(options.map((option) => [option.value, option.label]));

// Formats the `expected` and `actual` values of a condition outcome for reading: IDs and codes
// get the labels the editor offers for them, a code without a label loses its type prefix
// (MAKE_SKODA -> SKODA), and a price is an amount in the tenant currency.
export function useVehicleSelectionOutcomeFormatter() {
  const {tenantCurrency} = useTenant();
  const formatCurrency = useFormatCurrency();
  // Without a vehicle type and makes the make and model lists stay empty; their codes fall back to
  // the prefix-stripped code.
  const options = useVehicleSelectionFilterOptions(null, null);

  const labelsByAttribute = new Map<string, Map<string, string | null>>([
    ['BRANCH', toLabels(options.branchOptions)],
    ['SALE_PRESET', toLabels(options.salePresetOptions)],
    ['TAGS', toLabels(options.tagOptions)],
    ['VEHICLE_TYPE', toLabels(options.vehicleTypeOptions)],
    ['CONDITION', toLabels(options.vehicleConditionOptions)],
    ['FUEL_TYPE', toLabels(options.fuelTypeOptions)],
    ['BODY_STYLE', toLabels(options.bodyStyleOptions)],
  ]);

  const formatPrice = (minorAmount: number) =>
    formatCurrency(
      fromMinorUnits(minorAmount, tenantCurrency),
      tenantCurrency,
      getCurrencyFractionDigits(tenantCurrency)
    ) ?? minorAmount.toString();

  return (outcome: VehicleSelectionOutcomeResponseBody, value: unknown) => {
    const labels = labelsByAttribute.get(outcome.attribute);

    return formatVehicleSelectionOutcomeValue(value, {
      formatNumber: outcome.attribute === PRICE_ATTRIBUTE ? formatPrice : undefined,
      formatText: (text) => labels?.get(text) ?? stripVehicleSelectionCodePrefix(text),
    });
  };
}
